import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FormData {
  buildingType: string;
  surface: string;
  height: string;
  postalCode: string;
  city: string;
  name: string;
  company: string;
  phone: string;
  email: string;
}

interface PostalCodeSuggestion {
  code: string;
  city: string;
}

export default function StudyForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    buildingType: '',
    surface: '',
    height: '',
    postalCode: '',
    city: '',
    name: '',
    company: '',
    phone: '',
    email: '',
    rgpdConsent: false
  });
  const [postalCodeSuggestions, setPostalCodeSuggestions] = useState<PostalCodeSuggestion[]>([]);
  const [showPostalSuggestions, setShowPostalSuggestions] = useState(false);
  const [isValidatingPostal, setIsValidatingPostal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);

  // État pour gérer les interactions tactiles
  const [touchState, setTouchState] = useState({
    startY: 0,
    startX: 0,
    startTime: 0,
    hasMoved: false
  });

  const steps = [
    { 
      id: 'building', 
      title: 'Type de bâtiment', 
      icon: 'ri-building-line',
      description: 'Quel type de bâtiment souhaitez-vous équiper ?'
    },
    { 
      id: 'surface', 
      title: 'Surface approximative', 
      icon: 'ri-ruler-2-line',
      description: 'Quelle est la surface totale à éclairer ?'
    },
    { 
      id: 'height', 
      title: 'Hauteur de plafond', 
      icon: 'ri-expand-up-down-line',
      description: 'À quelle hauteur se trouve votre plafond ?'
    },
    { 
      id: 'location', 
      title: 'Localisation', 
      icon: 'ri-map-pin-line',
      description: 'Où se situe votre bâtiment ?'
    },
    { 
      id: 'contact', 
      title: 'Vos coordonnées', 
      icon: 'ri-user-line',
      description: 'Pour vous envoyer votre étude personnalisée'
    }
  ];

  const buildingTypes = [
    { id: 'warehouse', name: 'Entrepôts & Logistique', icon: 'ri-building-2-fill', gradient: 'from-blue-500 to-blue-600' },
    { id: 'factory', name: 'Usines & Industries', icon: 'ri-building-4-fill', gradient: 'from-orange-500 to-red-500' },
    { id: 'retail', name: 'Commerces & GMS', icon: 'ri-store-2-fill', gradient: 'from-emerald-500 to-teal-600' },
    { id: 'office', name: 'Bureaux & Tertiaire', icon: 'ri-briefcase-fill', gradient: 'from-purple-500 to-indigo-600' },
    { id: 'sports', name: 'Installations Sportives', icon: 'ri-football-fill', gradient: 'from-cyan-500 to-blue-500' },
    { id: 'healthcare', name: 'Établissements de Santé', icon: 'ri-hospital-fill', gradient: 'from-rose-500 to-pink-600' },
    { id: 'collectivity', name: 'Collectivités', icon: 'ri-government-fill', gradient: 'from-amber-500 to-orange-500' },
    { id: 'parking', name: 'Parkings & Sous-sols', icon: 'ri-parking-box-fill', gradient: 'from-slate-500 to-gray-600' },
  ];

  const surfaces = [
    { id: 'small', label: '< 500 m²', icon: 'ri-home-fill', range: 'Petit espace', gradient: 'from-green-500 to-emerald-600' },
    { id: 'medium', label: '500 - 2000 m²', icon: 'ri-building-2-fill', range: 'Espace moyen', gradient: 'from-blue-500 to-cyan-600' },
    { id: 'large', label: '2000 - 5000 m²', icon: 'ri-community-fill', range: 'Grand espace', gradient: 'from-purple-500 to-violet-600' },
    { id: 'xlarge', label: '> 5000 m²', icon: 'ri-building-4-fill', range: 'Très grand espace', gradient: 'from-orange-500 to-red-600' }
  ];

  const heights = [
    { id: 'low', label: '< 4 mètres', icon: 'ri-arrow-down-fill', gradient: 'from-emerald-500 to-green-600' },
    { id: 'medium', label: '4 - 8 mètres', icon: 'ri-subtract-line', gradient: 'from-blue-500 to-indigo-600' },
    { id: 'high', label: '8 - 12 mètres', icon: 'ri-arrow-up-fill', gradient: 'from-purple-500 to-violet-600' },
    { id: 'vhigh', label: '> 12 mètres', icon: 'ri-arrow-up-double-fill', gradient: 'from-orange-500 to-red-600' }
  ];

  // API pour valider les codes postaux - modifiée pour suggestions progressives
  const validatePostalCode = async (code: string) => {
    if (code.length < 1) {
      setPostalCodeSuggestions([]);
      setShowPostalSuggestions(false);
      return;
    }

    setIsValidatingPostal(true);
    try {
      // Pour les recherches partielles (1-4 chiffres), utiliser une API de recherche
      if (code.length <= 4) {
        // Simulation d'une API de codes postaux français avec recherche partielle
        const mockSuggestions = generateMockSuggestions(code);
        setPostalCodeSuggestions(mockSuggestions);
        setShowPostalSuggestions(mockSuggestions.length > 0);
      } else {
        // Pour les codes complets (5 chiffres), utiliser l'API Zippopotam
        const response = await fetch(`https://api.zippopotam.us/fr/${code}`);
        const data = await response.json();
        const suggestions: PostalCodeSuggestion[] = data.places?.map((place: any) => ({
          code: data['post code'],
          city: place['place name']
        })) || [];
          
        setPostalCodeSuggestions(suggestions);
        setShowPostalSuggestions(suggestions.length > 0);
      }
    } catch (error) {
      console.error('Erreur validation code postal:', error);
      // Utiliser les suggestions mockées en cas d'erreur
      const mockSuggestions = generateMockSuggestions(code);
      setPostalCodeSuggestions(mockSuggestions);
      setShowPostalSuggestions(mockSuggestions.length > 0);
    }
    setIsValidatingPostal(false);
  };

  // Génération de suggestions mockées pour les recherches partielles
  const generateMockSuggestions = (partialCode: string): PostalCodeSuggestion[] => {
    const mockData: { [key: string]: PostalCodeSuggestion[] } = {
      '1': [
        { code: '10000', city: 'Troyes' },
        { code: '11000', city: 'Carcassonne' },
        { code: '12000', city: 'Rodez' },
        { code: '13000', city: 'Marseille' },
        { code: '14000', city: 'Caen' },
        { code: '15000', city: 'Aurillac' },
        { code: '16000', city: 'Angoulême' },
        { code: '17000', city: 'La Rochelle' },
        { code: '18000', city: 'Bourges' },
        { code: '19000', city: 'Tulle' }
      ],
      '2': [
        { code: '20000', city: 'Ajaccio' },
        { code: '21000', city: 'Dijon' },
        { code: '22000', city: 'Saint-Brieuc' },
        { code: '23000', city: 'Guéret' },
        { code: '24000', city: 'Périgueux' },
        { code: '25000', city: 'Besançon' },
        { code: '26000', city: 'Valence' },
        { code: '27000', city: 'Évreux' },
        { code: '28000', city: 'Chartres' },
        { code: '29000', city: 'Quimper' }
      ],
      '3': [
        { code: '30000', city: 'Nîmes' },
        { code: '31000', city: 'Toulouse' },
        { code: '32000', city: 'Auch' },
        { code: '33000', city: 'Bordeaux' },
        { code: '34000', city: 'Montpellier' },
        { code: '35000', city: 'Rennes' },
        { code: '36000', city: 'Châteauroux' },
        { code: '37000', city: 'Tours' },
        { code: '38000', city: 'Grenoble' },
        { code: '39000', city: 'Lons-le-Saunier' }
      ],
      '4': [
        { code: '40000', city: 'Mont-de-Marsan' },
        { code: '41000', city: 'Blois' },
        { code: '42000', city: 'Saint-Étienne' },
        { code: '43000', city: 'Le Puy-en-Velay' },
        { code: '44000', city: 'Nantes' },
        { code: '45000', city: 'Orléans' },
        { code: '46000', city: 'Cahors' },
        { code: '47000', city: 'Agen' },
        { code: '48000', city: 'Mende' },
        { code: '49000', city: 'Angers' }
      ],
      '5': [
        { code: '50000', city: 'Saint-Lô' },
        { code: '51000', city: 'Châlons-en-Champagne' },
        { code: '52000', city: 'Chaumont' },
        { code: '53000', city: 'Laval' },
        { code: '54000', city: 'Nancy' },
        { code: '55000', city: 'Bar-le-Duc' },
        { code: '56000', city: 'Vannes' },
        { code: '57000', city: 'Metz' },
        { code: '58000', city: 'Nevers' },
        { code: '59000', city: 'Lille' }
      ],
      '6': [
        { code: '60000', city: 'Beauvais' },
        { code: '61000', city: 'Alençon' },
        { code: '62000', city: 'Arras' },
        { code: '63000', city: 'Clermont-Ferrand' },
        { code: '64000', city: 'Pau' },
        { code: '65000', city: 'Tarbes' },
        { code: '66000', city: 'Perpignan' },
        { code: '67000', city: 'Strasbourg' },
        { code: '68000', city: 'Colmar' },
        { code: '69000', city: 'Lyon' }
      ],
      '7': [
        { code: '70000', city: 'Vesoul' },
        { code: '71000', city: 'Mâcon' },
        { code: '72000', city: 'Le Mans' },
        { code: '73000', city: 'Chambéry' },
        { code: '74000', city: 'Annecy' },
        { code: '75001', city: 'Paris 1er' },
        { code: '75002', city: 'Paris 2e' },
        { code: '75003', city: 'Paris 3e' },
        { code: '76000', city: 'Rouen' },
        { code: '77000', city: 'Melun' }
      ],
      '8': [
        { code: '80000', city: 'Amiens' },
        { code: '81000', city: 'Albi' },
        { code: '82000', city: 'Montauban' },
        { code: '83000', city: 'Toulon' },
        { code: '84000', city: 'Avignon' },
        { code: '85000', city: 'La Roche-sur-Yon' },
        { code: '86000', city: 'Poitiers' },
        { code: '87000', city: 'Limoges' },
        { code: '88000', city: 'Épinal' },
        { code: '89000', city: 'Auxerre' }
      ],
      '9': [
        { code: '90000', city: 'Belfort' },
        { code: '91000', city: 'Évry' },
        { code: '92000', city: 'Nanterre' },
        { code: '93000', city: 'Bobigny' },
        { code: '94000', city: 'Créteil' },
        { code: '95000', city: 'Cergy' }
      ]
    };

    // Recherche par préfixe
    let suggestions: PostalCodeSuggestion[] = [];
    
    if (partialCode.length === 1) {
      suggestions = mockData[partialCode] || [];
    } else if (partialCode.length >= 2) {
      // Recherche dans tous les codes qui commencent par le préfixe
      Object.values(mockData).forEach(cities => {
        cities.forEach(city => {
          if (city.code.startsWith(partialCode)) {
            suggestions.push(city);
          }
        });
      });
    }

    // Limiter à 8 suggestions maximum
    return suggestions.slice(0, 8);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 Début de la soumission du formulaire');
    console.log('📋 Données brutes du formulaire:', formData);
    
    // ✅ VALIDATION STRICTE AVANT ENVOI
    const validationErrors: string[] = [];
    
    if (!formData.buildingType) validationErrors.push('Type de bâtiment manquant');
    if (!formData.surface) validationErrors.push('Surface manquante');
    if (!formData.height) validationErrors.push('Hauteur manquante');
    if (!formData.postalCode || formData.postalCode.length !== 5) validationErrors.push('Code postal invalide');
    if (!formData.city) validationErrors.push('Ville manquante');
    if (!formData.company) validationErrors.push('Société manquante');
    if (!formData.name) validationErrors.push('Nom manquant');
    if (!formData.phone || formData.phone.length < 10) validationErrors.push('Téléphone invalide');
    if (!formData.email || !formData.email.includes('@')) validationErrors.push('Email invalide');
    
    if (validationErrors.length > 0) {
      console.error('❌ ERREURS DE VALIDATION:', validationErrors);
      alert('⚠️ Formulaire incomplet :\n\n' + validationErrors.join('\n'));
      return;
    }
    
    console.log('✅ Validation réussie');
    
    setIsSubmitting(true);
    
    const submitUrl = 'https://readdy.ai/api/form/d4ugh712tio6iafo0c30';
    
    // 🔄 CONVERSION DES IDS EN LABELS FRANÇAIS
    const buildingTypeLabels: { [key: string]: string } = {
      'warehouse': 'Entrepôts & Logistique',
      'factory': 'Usines & Industries',
      'retail': 'Commerces & GMS',
      'office': 'Bureaux & Tertiaire',
      'sports': 'Installations Sportives',
      'healthcare': 'Établissements de Santé',
      'collectivity': 'Collectivités',
      'parking': 'Parkings & Sous-sols'
    };
    
    const surfaceLabels: { [key: string]: string } = {
      'small': '< 500 m²',
      'medium': '500 - 2000 m²',
      'large': '2000 - 5000 m²',
      'xlarge': '> 5000 m²'
    };
    
    const heightLabels: { [key: string]: string } = {
      'low': '< 4 mètres',
      'medium': '4 - 8 mètres',
      'high': '8 - 12 mètres',
      'vhigh': '> 12 mètres'
    };
    
    const buildingTypeLabel = buildingTypeLabels[formData.buildingType] || formData.buildingType;
    const surfaceLabel = surfaceLabels[formData.surface] || formData.surface;
    const heightLabel = heightLabels[formData.height] || formData.height;
    
    console.log('🔄 Conversion des données:', {
      buildingType: `${formData.buildingType} → ${buildingTypeLabel}`,
      surface: `${formData.surface} → ${surfaceLabel}`,
      height: `${formData.height} → ${heightLabel}`
    });
    
    // Créer les données du formulaire au format URLSearchParams
    const formDataToSubmit = new URLSearchParams();
    
    // ✅ ENVOYER LES LABELS FRANÇAIS COMPLETS
    formDataToSubmit.append('buildingType', buildingTypeLabel);
    formDataToSubmit.append('surface', surfaceLabel);
    formDataToSubmit.append('height', heightLabel);
    formDataToSubmit.append('postalCode', formData.postalCode);
    formDataToSubmit.append('city', formData.city);
    formDataToSubmit.append('company', formData.company);
    formDataToSubmit.append('name', formData.name);
    formDataToSubmit.append('phone', formData.phone);
    formDataToSubmit.append('email', formData.email);

    console.log('📤 Données finales à envoyer:', formDataToSubmit.toString());
    console.log('🌐 URL de soumission:', submitUrl);

    // ✅ TENTATIVE D'ENVOI AVEC RETRY AUTOMATIQUE
    let attempt = 0;
    const maxAttempts = 3;
    let lastError: any = null;

    while (attempt < maxAttempts) {
      attempt++;
      console.log(`⏳ Tentative ${attempt}/${maxAttempts}...`);
      
      try {
        const response = await fetch(submitUrl, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json, text/plain, */*'
          },
          body: formDataToSubmit.toString()
        });

        console.log('📡 Réponse reçue:', {
          tentative: attempt,
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: {
            contentType: response.headers.get('content-type'),
            cors: response.headers.get('access-control-allow-origin')
          }
        });

        // Lire le corps de la réponse
        const responseText = await response.text();
        console.log('📄 Corps de la réponse:', responseText);

        if (response.ok) {
          console.log('✅✅✅ SUCCÈS ! Le lead a été enregistré !');
          
          setShowSuccessPage(true);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
          
          setIsSubmitting(false);
          return;
        } else {
          console.error(`❌ Erreur HTTP ${response.status}:`, responseText);
          
          lastError = {
            status: response.status,
            statusText: response.statusText,
            body: responseText
          };
          
          // Si erreur 400, afficher les détails et arrêter
          if (response.status === 400) {
            console.error('❌ ERREUR 400 - Données refusées par le serveur');
            console.error('❌ Données envoyées:', {
              buildingType: buildingTypeLabel,
              surface: surfaceLabel,
              height: heightLabel,
              postalCode: formData.postalCode,
              city: formData.city,
              company: formData.company,
              name: formData.name,
              phone: formData.phone,
              email: formData.email
            });
            
            let errorMessage = `Erreur ${response.status}`;
            try {
              const errorData = JSON.parse(responseText);
              if (errorData.message) {
                errorMessage = errorData.message;
              }
            } catch (e) {
              if (responseText) {
                errorMessage = responseText;
              }
            }
            
            alert(`❌ Erreur lors de l'envoi du formulaire (${response.status}).\n\nDétails: ${errorMessage}\n\n⚠️ Cette erreur a été enregistrée. Veuillez nous contacter au 01 XX XX XX XX en mentionnant cette erreur.`);
            setIsSubmitting(false);
            return;
          }
          
          // Pour les autres erreurs, réessayer
          if (attempt < maxAttempts) {
            console.log(`⏳ Nouvelle tentative dans 2 secondes...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        
      } catch (error) {
        console.error(`❌ Erreur réseau (tentative ${attempt}/${maxAttempts}):`, error);
        lastError = error;
        
        if (attempt < maxAttempts) {
          console.log(`⏳ Nouvelle tentative dans 2 secondes...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    
    // ❌ Échec après toutes les tentatives
    console.error('❌❌❌ ÉCHEC APRÈS', maxAttempts, 'TENTATIVES');
    console.error('❌ Dernière erreur:', lastError);
    
    alert(`❌ Impossible d'envoyer le formulaire après ${maxAttempts} tentatives.\n\nVérifiez votre connexion internet et réessayez.\n\nSi le problème persiste, contactez-nous directement au 01 XX XX XX XX.\n\nCode d'erreur: ${lastError?.status || 'NETWORK_ERROR'}`);
    
    setIsSubmitting(false);
  };

  // Fonction pour recommencer
  const handleRestart = () => {
    setShowSuccessPage(false);
    setFormData({
      buildingType: '',
      surface: '',
      height: '',
      postalCode: '',
      city: '',
      name: '',
      company: '',
      phone: '',
      email: '',
      rgpdConsent: false
    });
    setCurrentStep(0);
  };

  // Utilisation de useCallback pour éviter les re-créations de fonction
  const selectOption = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Avancer immédiatement à l'étape suivante
    setTimeout(() => {
      setCurrentStep(prev => prev < steps.length - 1 ? prev + 1 : prev);
    }, 0);
  }, [steps.length]);

  const updateField = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({...prev, [field]: value}));
    
    // Validation des codes postaux en temps réel
    if (field === 'postalCode') {
      validatePostalCode(value);
    }
  }, []);

  // Gestionnaire de début de touch
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchState({
      startY: touch.clientY,
      startX: touch.clientX,
      startTime: Date.now(),
      hasMoved: false
    });
  }, []);

  // Gestionnaire de mouvement touch
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const deltaY = Math.abs(touch.clientY - touchState.startY);
    const deltaX = Math.abs(touch.clientX - touchState.startX);
    
    // Si le mouvement dépasse 10px, on considère que c'est un scroll
    if (deltaY > 10 || deltaX > 10) {
      setTouchState(prev => ({ ...prev, hasMoved: true }));
    }
  }, [touchState.startY, touchState.startX]);

  // Gestionnaire de fin de touch
  const handleTouchEnd = useCallback((field: keyof FormData, value: string) => {
    return (e: React.TouchEvent) => {
      const touchDuration = Date.now() - touchState.startTime;
      
      // Seulement si :
      // 1. Le touch n'a pas bougé (pas de scroll)
      // 2. La durée est raisonnable (pas un long press)
      if (!touchState.hasMoved && touchDuration < 500) {
        e.preventDefault();
        e.stopPropagation();
        
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Avancer à l'étape suivante
        if (currentStep < steps.length - 1) {
          setTimeout(() => {
            setCurrentStep(prev => prev + 1);
          }, 150);
        }
      }
      
      // Reset du state touch
      setTouchState({
        startY: 0,
        startX: 0,
        startTime: 0,
        hasMoved: false
      });
    };
  }, [touchState.hasMoved, touchState.startTime, currentStep, steps.length]);

  // Gestionnaire de clic pour desktop
  const handleClick = useCallback((field: keyof FormData, value: string) => {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Avancer à l'étape suivante
      if (currentStep < steps.length - 1) {
        setTimeout(() => {
          setCurrentStep(prev => prev + 1);
        }, 150);
      }
    };
  }, [currentStep, steps.length]);

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const selectPostalSuggestion = (suggestion: PostalCodeSuggestion) => {
    setFormData(prev => ({
      ...prev,
      postalCode: suggestion.code,
      city: suggestion.city
    }));
    setShowPostalSuggestions(false);
  };

  // Si la page de succès doit être affichée
  if (showSuccessPage) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 pt-20 sm:pt-32 pb-16 min-h-screen flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl mx-auto"
        >
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-6 sm:p-8 lg:p-12 text-center">
            {/* Icône de succès animée */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.2 
              }}
              className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-2xl"
            >
              <motion.i
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="ri-checkbox-circle-fill text-4xl sm:text-5xl text-white"
              ></motion.i>
            </motion.div>

            {/* Titre */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-2xl sm:text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600 mb-3 sm:mb-4"
            >
              Demande envoyée avec succès !
            </motion.h1>

            {/* Message */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-base sm:text-lg text-slate-700 mb-6 sm:mb-8 leading-relaxed px-2"
            >
              Merci <strong>{formData.name}</strong> pour votre confiance ! 🎉
              <br />
              Votre demande d'étude gratuite a bien été enregistrée.
            </motion.p>

            {/* Informations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-emerald-200/50"
            >
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 text-left">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                  <i className="ri-time-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-bold text-slate-900 mb-2 text-sm sm:text-base">Prochaines étapes :</h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-slate-700">
                    <li className="flex items-center gap-2 justify-center sm:justify-start">
                      <i className="ri-check-line text-emerald-600 flex-shrink-0"></i>
                      <span>Un expert vous contactera sous <strong>24h</strong></span>
                    </li>
                    <li className="flex items-center gap-2 justify-center sm:justify-start">
                      <i className="ri-check-line text-emerald-600 flex-shrink-0"></i>
                      <span>Analyse personnalisée de votre projet</span>
                    </li>
                    <li className="flex items-center gap-2 justify-center sm:justify-start">
                      <i className="ri-check-line text-emerald-600 flex-shrink-0"></i>
                      <span>Devis détaillé avec financement CEE</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Bouton d'action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex justify-center"
            >
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold py-3 px-6 sm:px-8 rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 shadow-lg hover:shadow-xl whitespace-nowrap text-sm sm:text-base"
              >
                <i className="ri-home-line text-base sm:text-lg"></i>
                <span>Retour à l'accueil</span>
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 pt-20 sm:pt-32 pb-8 min-h-screen">
      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-emerald-200/30 to-teal-300/30 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-gradient-to-br from-blue-200/30 to-purple-300/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 w-full max-w-lg mx-auto px-4">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-4 sm:mb-6"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-xl rounded-full px-4 py-2 shadow-md border border-white/50 mb-4">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-xs font-semibold text-slate-600">Étude 100% gratuite</span>
          </div>
          
          <h1 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 mb-4 leading-tight">
            Votre projet LED
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500">
              clés en main
            </span>
          </h1>
          
          {/* Enhanced subtitle with better design */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-emerald-500 rounded-full"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full px-2.5 py-1"
            >
              <p className="text-[10px] sm:text-xs text-slate-700 font-medium">
                Moins de 2 min à remplir
              </p>
            </motion.div>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
              className="w-1.5 h-1.5 bg-teal-500 rounded-full"
            />
          </div>

          {/* 🎯 OFFRE 1€ - Bloc principal très visible */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-4 mb-4 shadow-2xl border-2 border-white/40 overflow-hidden"
          >
            {/* ❄️ Effet de neige qui tombe - Flocons élégants */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
              {[...Array(35)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute text-white/90"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `-${Math.random() * 20}%`,
                    fontSize: `${6 + Math.random() * 10}px`,
                    filter: 'drop-shadow(0 1px 2px rgba(255,255,255,0.5))',
                  }}
                  animate={{
                    y: ['0vh', '120vh'],
                    x: [0, Math.random() * 30 - 15],
                    opacity: [0, 0.9, 0.9, 0.7, 0],
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 8 + Math.random() * 6,
                    repeat: Infinity,
                    delay: Math.random() * 8,
                    ease: "linear",
                  }}
                >
                  ✻
                </motion.div>
              ))}
            </div>

            {/* Flèche gauche animée - DANS la bulle */}
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute bottom-4 left-4 sm:left-8 text-white/40"
            >
              <i className="ri-arrow-down-line text-4xl sm:text-5xl"></i>
            </motion.div>

            {/* Flèche droite animée - DANS la bulle */}
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
              className="absolute bottom-4 right-4 sm:right-8 text-white/40"
            >
              <i className="ri-arrow-down-line text-4xl sm:text-5xl"></i>
            </motion.div>

            <div className="text-center relative z-20">
              <div className="text-white/90 text-sm font-semibold mb-2 drop-shadow-lg" style={{ textShadow: '0 3px 12px rgba(0,0,0,0.3), 0 1px 4px rgba(0,0,0,0.2)' }}>Installation LED complète dès</div>
              <div className="flex items-center justify-center gap-3">
                <span className="text-6xl font-black text-white drop-shadow-2xl" style={{ textShadow: '0 6px 20px rgba(0,0,0,0.4), 0 3px 8px rgba(0,0,0,0.3), 0 1px 4px rgba(0,0,0,0.2)' }}>1€</span>
                <div className="text-left">
                  <div className="text-white/90 text-xs drop-shadow-md" style={{ textShadow: '0 3px 10px rgba(0,0,0,0.25), 0 1px 4px rgba(0,0,0,0.15)' }}>symbolique</div>
                  <div className="text-white font-bold text-sm drop-shadow-lg" style={{ textShadow: '0 3px 12px rgba(0,0,0,0.35), 0 1px 4px rgba(0,0,0,0.2)' }}>Grâce au CEE</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 border-2 border-white/40 shadow-lg">
                <motion.i 
                  className="ri-flashlight-fill text-yellow-300 text-sm drop-shadow-md" 
                  style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.25))' }}
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, -10, 10, 0]
                  }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                ></motion.i>
                <span className="text-white text-xs font-semibold drop-shadow-md" style={{ textShadow: '0 3px 10px rgba(0,0,0,0.3), 0 1px 4px rgba(0,0,0,0.15)' }}>Financement jusqu'à 100%</span>
              </div>
            </div>
          </motion.div>

          {/* ⚡ Bénéfices concrets - Version ultra compacte en ligne */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap items-center justify-center gap-3 mb-4"
          >
            <motion.div 
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-amber-50 to-orange-50 backdrop-blur-xl rounded-full px-3 py-1.5 border border-amber-200/50 shadow-sm"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center"
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <i className="ri-percent-line text-white text-[10px]"></i>
              </motion.div>
              <span className="text-xs font-bold bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent">Économies 75%</span>
            </motion.div>

            <motion.div 
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-blue-50 to-cyan-50 backdrop-blur-xl rounded-full px-3 py-1.5 border border-blue-200/50 shadow-sm"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center"
                animate={{ 
                  y: [0, -3, 0],
                }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.2
                }}
              >
                <i className="ri-tools-line text-white text-[10px]"></i>
              </motion.div>
              <span className="text-xs font-bold bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">Installation incluse</span>
            </motion.div>

            <motion.div 
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-purple-50 to-pink-50 backdrop-blur-xl rounded-full px-3 py-1.5 border border-purple-200/50 shadow-sm"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center"
                animate={{ 
                  scale: [1, 1.15, 1],
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.4
                }}
              >
                <i className="ri-shield-check-line text-white text-[10px]"></i>
              </motion.div>
              <span className="text-xs font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Garantie 5 ans</span>
            </motion.div>

            <motion.div 
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-rose-50 to-red-50 backdrop-blur-xl rounded-full px-3 py-1.5 border border-rose-200/50 shadow-sm"
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="w-5 h-5 rounded-full bg-gradient-to-br from-rose-400 to-red-500 flex items-center justify-center"
                animate={{ 
                  rotate: [0, 360],
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <i className="ri-file-check-line text-white text-[10px]"></i>
              </motion.div>
              <span className="text-xs font-bold bg-gradient-to-r from-rose-700 to-red-600 bg-clip-text text-transparent">Dossier CEE géré</span>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Form Container */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/50"
        >
          <div className="p-4 sm:p-6">
            {/* Step Content */}
            <div className="min-h-[280px]">
              {/* Step Header with Question */}
              <div className="mb-6 text-center relative">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-md">
                    <i className={`${steps[currentStep].icon} text-sm text-white`}></i>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                    Étape {currentStep + 1} sur {steps.length}
                  </span>
                </div>
                
                <h2 className="text-xl font-black text-slate-900 mb-1">
                  {steps[currentStep].title}
                </h2>
                
                {/* Sous-titre descriptif subtil */}
                {currentStep < 3 && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-xs text-slate-500 font-medium"
                  >
                    {steps[currentStep].description}
                  </motion.p>
                )}
              </div>

              {currentStep === 0 && (
                <div
                  key="building-type"
                  style={{ opacity: 1 }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {buildingTypes.map((type) => (
                      <div
                        key={type.id}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd('buildingType', type.id)}
                        onClick={handleClick('buildingType', type.id)}
                        className="group relative p-3 rounded-xl border text-center transition-all duration-200 cursor-pointer min-h-[80px] flex flex-col items-center justify-center overflow-hidden whitespace-nowrap select-none border-slate-200 bg-white/80 shadow-lg md:hover:shadow-2xl md:hover:border-emerald-300 md:hover:-translate-y-1 active:scale-95 active:bg-emerald-50"
                      >
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${type.gradient} flex items-center justify-center mb-1.5 shadow-md pointer-events-none`}>
                          <i className={`${type.icon} text-white text-xs`}></i>
                        </div>
                        <span className="text-xs font-medium text-slate-900 pointer-events-none">
                          {type.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div
                  key="surface-area"
                  style={{ opacity: 1 }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {surfaces.map((area) => (
                      <div
                        key={area.id}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd('surface', area.id)}
                        onClick={handleClick('surface', area.id)}
                        className="group relative p-3 rounded-xl border text-center transition-all duration-200 cursor-pointer min-h-[80px] flex flex-col items-center justify-center overflow-hidden whitespace-nowrap select-none border-slate-200 bg-white/80 shadow-lg md:hover:shadow-2xl md:hover:border-emerald-300 md:hover:-translate-y-1 active:scale-95 active:bg-emerald-50"
                      >
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${area.gradient} flex items-center justify-center mb-1.5 shadow-md pointer-events-none`}>
                          <i className={`${area.icon} text-white text-xs`}></i>
                        </div>
                        <span className="text-xs font-medium text-slate-900 pointer-events-none">
                          {area.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div
                  key="ceiling-height"
                  style={{ opacity: 1 }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    {heights.map((height) => (
                      <div
                        key={height.id}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd('height', height.id)}
                        onClick={handleClick('height', height.id)}
                        className="group relative p-3 rounded-xl border text-center transition-all duration-200 cursor-pointer min-h-[80px] flex flex-col items-center justify-center overflow-hidden whitespace-nowrap select-none border-slate-200 bg-white/80 shadow-lg md:hover:shadow-2xl md:hover:border-emerald-300 md:hover:-translate-y-1 active:scale-95 active:bg-emerald-50"
                      >
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${height.gradient} flex items-center justify-center mb-1.5 shadow-md pointer-events-none`}>
                          <i className={`${height.icon} text-white text-xs`}></i>
                        </div>
                        <span className="text-xs font-medium text-slate-900 pointer-events-none">
                          {height.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="space-y-4">
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className="block text-xs font-bold text-slate-700 mb-2">
                        Code postal *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formData.postalCode}
                          onChange={(e) => updateField('postalCode', e.target.value)}
                          placeholder="Ex: 75001"
                          className="w-full px-3 py-2.5 border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all text-base bg-white/80"
                          style={{ fontSize: '16px', transformOrigin: 'left' }}
                          maxLength={5}
                          pattern="[0-9]{5}"
                          autoComplete="postal-code"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                          {isValidatingPostal ? (
                            <i className="ri-loader-4-line text-xs text-white animate-spin"></i>
                          ) : (
                            <i className="ri-map-pin-line text-xs text-white"></i>
                          )}
                        </div>
                        
                        {/* Suggestions dropdown */}
                        {showPostalSuggestions && postalCodeSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                            {postalCodeSuggestions.map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => selectPostalSuggestion(suggestion)}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 transition-colors text-sm border-b border-slate-100 last:border-b-0"
                              >
                                <span className="font-medium">{suggestion.code}</span> - {suggestion.city}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <label className="block text-xs font-bold text-slate-700 mb-2">
                        Ville *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => updateField('city', e.target.value)}
                          placeholder="Ex: Paris"
                          className="w-full px-3 py-2.5 border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all text-base bg-white/80"
                          style={{ fontSize: '16px', transformOrigin: 'left' }}
                          autoComplete="address-level2"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <i className="ri-building-line text-xs text-white"></i>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Bouton Suivant pour l'étape localisation */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                      className="pt-4"
                    >
                      <button
                        type="button"
                        onClick={goToNextStep}
                        disabled={!formData.postalCode || !formData.city}
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold py-3 px-6 rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <span>Continuer</span>
                        <i className="ri-arrow-right-line text-sm"></i>
                      </button>
                    </motion.div>
                  </div>
                </form>
              )}

              {/* Step 4: Contact Form */}
              {currentStep === 4 && (
                <form
                  id="etude-led-gratuite"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="space-y-3">
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className="block text-xs font-bold text-slate-700 mb-2">
                        Société *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={(e) => updateField('company', e.target.value)}
                          placeholder="Nom de l'entreprise"
                          className="w-full px-3 py-2.5 border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all text-base bg-white/80"
                          style={{ fontSize: '16px', transformOrigin: 'left' }}
                          autoComplete="organization"
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                          <i className="ri-building-line text-xs text-white"></i>
                        </div>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <label className="block text-xs font-bold text-slate-700 mb-2">
                        Nom *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={(e) => updateField('name', e.target.value)}
                          placeholder="Votre nom"
                          className="w-full px-3 py-2.5 border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all text-base bg-white/80"
                          style={{ fontSize: '16px', transformOrigin: 'left' }}
                          autoComplete="name"
                          required
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center">
                          <i className="ri-user-line text-xs text-white"></i>
                        </div>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <label className="block text-xs font-bold text-slate-700 mb-2">
                        Téléphone *
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          inputMode="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={(e) => updateField('phone', e.target.value)}
                          placeholder="06 12 34 56 78"
                          className="w-full px-3 py-2.5 border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all text-base bg-white/80"
                          style={{ fontSize: '16px', transformOrigin: 'left' }}
                          autoComplete="tel"
                          required
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                          <i className="ri-phone-line text-xs text-white"></i>
                        </div>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 }}
                    >
                      <label className="block text-xs font-bold text-slate-700 mb-2">
                        Email *
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          inputMode="email"
                          name="email"
                          value={formData.email}
                          onChange={(e) => updateField('email', e.target.value)}
                          placeholder="votre@email.fr"
                          className="w-full px-3 py-2.5 border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all text-base bg-white/80"
                          style={{ fontSize: '16px', transformOrigin: 'left' }}
                          autoComplete="email"
                          required
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                          <i className="ri-mail-line text-xs text-white"></i>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Case à cocher RGPD */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.rgpdConsent}
                          onChange={(e) => setFormData({ ...formData, rgpdConsent: e.target.checked })}
                          className="mt-1 w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 focus:ring-2 cursor-pointer"
                          required
                        />
                        <div className="text-xs text-slate-700 leading-relaxed">
                          <span className="font-semibold">J'accepte le traitement de mes données personnelles *</span>
                          <p className="mt-1 text-xs">
                            En cochant, j'accepte que Power Reno Pro traite mes données pour me recontacter. 
                            <a 
                              href="/politique-de-confidentialite" 
                              target="_blank"
                              className="text-emerald-600 hover:text-emerald-700 font-medium underline cursor-pointer ml-1"
                            >
                              Politique de confidentialité
                            </a>
                          </p>
                        </div>
                      </label>
                    </motion.div>
                    
                    {/* Bouton d'envoi */}
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                      className="pt-4"
                    >
                      <button
                        type="submit"
                        disabled={!formData.name || !formData.phone || !formData.email || !formData.rgpdConsent || isSubmitting}
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold py-3 px-6 rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap group"
                      >
                        {isSubmitting ? (
                          <>
                            <i className="ri-loader-4-line text-sm animate-spin"></i>
                            <span>Envoi en cours...</span>
                          </>
                        ) : (
                          <>
                            <motion.i 
                              className="ri-send-plane-2-fill text-sm"
                              animate={
                                (formData.name && formData.phone && formData.email && formData.rgpdConsent) 
                                  ? {
                                      x: [0, 3, 0],
                                      rotate: [0, 15, -5, 0],
                                      scale: [1, 1.1, 1]
                                    }
                                  : {}
                              }
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                repeatDelay: 0.5,
                                ease: "easeInOut"
                              }}
                            ></motion.i>
                            <span className="relative">
                              LED GO
                              {(formData.name && formData.phone && formData.email && formData.rgpdConsent) && (
                                <motion.span
                                  className="absolute -top-1 -right-2 w-2 h-2 bg-yellow-400 rounded-full"
                                  animate={{
                                    scale: [0, 1, 0],
                                    opacity: [0, 1, 0]
                                  }}
                                  transition={{
                                    duration: 1,
                                    repeat: Infinity,
                                    repeatDelay: 0.3
                                  }}
                                ></motion.span>
                              )}
                            </span>
                          </>
                        )}
                      </button>
                    </motion.div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-4 sm:mt-6"
        >
          {[
            { 
              icon: 'ri-time-fill', 
              text: 'Réponse sous 24h', 
              gradient: 'from-blue-500 to-cyan-500',
              borderColor: 'border-blue-200/50',
              bgColor: 'bg-gradient-to-br from-blue-50/80 to-cyan-50/60'
            },
            { 
              icon: 'ri-gift-fill', 
              text: 'Étude 100% gratuite', 
              gradient: 'from-emerald-500 to-teal-500',
              borderColor: 'border-emerald-200/50',
              bgColor: 'bg-gradient-to-br from-emerald-50/80 to-teal-50/60'
            },
            { 
              icon: 'ri-user-star-fill', 
              text: 'Expert dédié', 
              gradient: 'from-amber-500 to-orange-500',
              borderColor: 'border-amber-200/50',
              bgColor: 'bg-gradient-to-br from-amber-50/80 to-orange-50/60'
            }
          ].map((indicator, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
              whileHover={{ 
                scale: 1.02,
                y: -1,
                transition: { duration: 0.2 }
              }}
              className={`relative ${indicator.bgColor} ${indicator.borderColor} border rounded-lg p-2 sm:p-3 group cursor-pointer overflow-hidden`}
            >
              {/* Progress bar animation */}
              <motion.div
                className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${indicator.gradient} rounded-full`}
                initial={{ width: "0%" }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.6 + index * 0.2, ease: "easeOut" }}
              />
              
              <div className="flex items-center space-x-2 relative z-10">
                <motion.div 
                  className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br ${indicator.gradient} flex items-center justify-center shadow-sm`}
                  whileHover={{ 
                    scale: 1.1,
                    rotate: [0, -5, 5, 0],
                    transition: { duration: 0.3 }
                  }}
                >
                  <i className={`${indicator.icon} text-white text-xs sm:text-sm`}></i>
                </motion.div>
                <span className="text-[10px] sm:text-xs font-medium text-slate-700 group-hover:text-slate-900 transition-colors leading-tight">
                  {indicator.text}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
